import { type AnalyticsAdapter, LoggerAnalyticsAdapter } from '@nx/analytics-service';
import { APP_SCOPE } from '@nx/constants';
import { type AbstractLogger, NullLogger } from '@nx/logger';
import type { TokenProviders } from '@nx/neo4j-token-provider';
import type { Action, Reducer } from '@reduxjs/toolkit';
import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query/react';
import { combineReducers } from 'redux';
import type { PersistMigrate, Persistor } from 'redux-persist';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  createMigrate,
  persistReducer,
  persistStore,
} from 'redux-persist';
import type { PersistPartial } from 'redux-persist/lib/persistReducer';

import type { Neo4jDriverAdapter } from './adapters/neo4j-driver-adapter';
import { neo4jDriverAdapter } from './adapters/neo4j-driver-adapter-instance';
import createAnalyticsEventsMiddleware from './middlewares/analytics-events';
import postConnectionMiddleware from './middlewares/connections';
import { eventListenerMiddleware } from './middlewares/state-event-listeners';
import { Persist } from './persist/persist';
import agentApi from './slices/agents/agents.api';
import appContextReducer from './slices/app-context-slice';
import * as Capabilities from './slices/capabilities-slice';
import * as Configuration from './slices/configuration/configuration-slice';
import { createConfigurationListenerMiddleware } from './slices/configuration/listeners';
import { migrateConnections } from './slices/connections/connections-migration';
import connections from './slices/connections/connections-slice';
import { createConnectionMiddleware } from './slices/connections/listeners';
import consoleApi, { createAuraApiListenerMiddleware } from './slices/console/console.api';
import dataScienceApi from './slices/data-science/data-science.api';
import dataSummary from './slices/data-summary/data-summary-slice';
import * as Desktop from './slices/desktop/desktop';
import relateApi from './slices/desktop/relate.api';
import * as FeatureFlags from './slices/feature-flags-slice';
import fleetManagementApi from './slices/fleet-management/fleet-management.api';
import * as genaiApi from './slices/genai-api-slice';
import graphStyling, { GRAPH_STYLE_PERSISTED_KEYS } from './slices/graph-styling/graph-styling-slice';
import { stylingPrecedenceMiddleware } from './slices/graph-styling/styling-precedence.middleware';
import graphqlApi from './slices/graphql/graphql.api';
import * as GuidePlaylists from './slices/guides/guide-playlists-slice';
import { migrateGuides } from './slices/guides/guides-migration';
import guides, { GUIDES_PERSISTED_KEYS } from './slices/guides/guides-slice';
import guidesApi from './slices/guides/guides.api';
import { createGuidesMiddleware } from './slices/guides/listeners';
import metadata from './slices/metadata-slice';
import modals from './slices/modals-slice';
import network, { networkDown, networkUp } from './slices/network-slice';
import notifications, { NOTIFICATIONS_PERSISTED_KEYS } from './slices/notifications-slice';
import opsContextReducer from './slices/ops-context-slice';
import opsApi from './slices/ops/ops.api';
import * as Session from './slices/session-slice';
import settings, { SETTINGS_PERSISTED_KEYS, migrateSettings } from './slices/settings-slice';
import { sharedStorageApi } from './slices/shared-storage/shared-storage.api';
import sidebar, { SIDEBAR_PERSISTED_KEYS } from './slices/sidebar-slice';
import uiElements, { UIELEMENTS_PERSISTED_KEYS } from './slices/ui-elements-slice';
import upload from './slices/upload-slice';

const GLOBAL_STORE_SYMBOL: unique symbol = Symbol('Global Redux store');

type GlobalStoreInstance = {
  globalPersistor: Persistor;
  globalStore: Store;
};

declare global {
  interface Window {
    [GLOBAL_STORE_SYMBOL]?: GlobalStoreInstance;
  }
}

/**
 * Expose Redux store instance globally
 */
function exposeStoreInstance(instance: GlobalStoreInstance): void {
  Object.defineProperty(
    window,
    // Use private unique symbol to force property access via the helper
    GLOBAL_STORE_SYMBOL,
    {
      // Disallow property type to be changed and property to be removed
      configurable: false,
      // Do not let this property show up during enumeration
      enumerable: false,
      // Do not allow overwriting this property, i.e. allow exposing store once
      writable: false,
      value: instance,
    },
  );
}

/**
 * Retrieve previously exposed Redux store instance
 *
 * This is an escape-hatch API provided for backwards-compatibility.
 * Prefer using dependency injection to get access to the store.
 */
export function UNSAFE_getGlobalStoreInstance(): GlobalStoreInstance {
  if (window[GLOBAL_STORE_SYMBOL] === undefined) {
    throw new Error('The store instance is not exposed (yet?)');
  }

  return window[GLOBAL_STORE_SYMBOL];
}

const storage = new Persist(APP_SCOPE.framework);

function setupPersist<S, A extends Action>(
  reducer: Reducer<S, A>,
  name: string,
  persistedKeys: string[],
  migrate?: PersistMigrate,
): Reducer<S & PersistPartial, A> {
  return persistReducer(
    {
      key: name,
      storage,
      version: 1,
      throttle: 100,
      whitelist: persistedKeys,
      keyPrefix: '',
      migrate,
    },
    reducer,
  );
}

// TODO: This is exported for testing only, fix tests and remove import
export function createReducer() {
  return combineReducers({
    [consoleApi.reducerPath]: consoleApi.reducer,
    modals,
    [relateApi.reducerPath]: relateApi.reducer,
    appContext: appContextReducer,
    [Capabilities.reducerPath]: Capabilities.reducer,
    [Configuration.reducerPath]: setupPersist(
      Configuration.reducer,
      'configuration',
      Configuration.CONFIGURATION_PERSISTED_KEYS,
      Configuration.migrateConfiguration,
    ),
    connections: persistReducer(
      {
        key: 'connections',
        storage,
        version: 3,
        throttle: 100,
        whitelist: ['activeConnectionId', 'connections', 'settings'],
        keyPrefix: '',
        migrate: migrateConnections,
      },
      connections.reducer,
    ),
    dataSummary,
    [Desktop.reducerPath]: setupPersist(Desktop.reducer, 'desktop', Desktop.DESKTOP_PERSISTED_KEYS),
    [FeatureFlags.reducerPath]: setupPersist(
      FeatureFlags.reducer,
      'featureFlags',
      FeatureFlags.FEATURE_FLAGS_PERSISTED_KEYS,
    ),
    guides: setupPersist(guides, 'guides', GUIDES_PERSISTED_KEYS, migrateGuides),
    [GuidePlaylists.reducerPath]: setupPersist(
      GuidePlaylists.reducer,
      'guidePlaylists',
      GuidePlaylists.GUIDE_PLAYLISTS_PERSISTED_KEYS,
    ),
    [GuidePlaylists.guidePlaylistsApi.reducerPath]: GuidePlaylists.guidePlaylistsApi.reducer,
    metadata,
    notifications: setupPersist(notifications, 'notifications', NOTIFICATIONS_PERSISTED_KEYS),
    session: Session.reducer,
    settings: persistReducer(
      {
        key: 'settings',
        storage,
        version: 2,
        throttle: 100,
        whitelist: SETTINGS_PERSISTED_KEYS,
        keyPrefix: '',
        // @ts-ignore redux-persist types are not good enough https://github.com/rt2zz/redux-persist/issues/1065#issuecomment-554538845
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        migrate: createMigrate(migrateSettings),
      },
      settings,
    ),
    sidebar: setupPersist(sidebar, 'sidebar', SIDEBAR_PERSISTED_KEYS),
    uiElements: setupPersist(uiElements, 'uiElements', UIELEMENTS_PERSISTED_KEYS),
    graphStyling: setupPersist(graphStyling, 'graphStyling', GRAPH_STYLE_PERSISTED_KEYS),
    network,
    [guidesApi.reducerPath]: guidesApi.reducer,
    [genaiApi.reducerPath]: genaiApi.reducer,
    [opsApi.reducerPath]: opsApi.reducer,
    [fleetManagementApi.reducerPath]: fleetManagementApi.reducer,
    [graphqlApi.reducerPath]: graphqlApi.reducer,
    opsContext: opsContextReducer,
    [dataScienceApi.reducerPath]: dataScienceApi.reducer,
    [agentApi.reducerPath]: agentApi.reducer,
    upload: upload,
    [sharedStorageApi.reducerPath]: sharedStorageApi.reducer,
  });
}

export function createStore({
  adapters,
  initialData,
  meta,
}: {
  adapters: {
    analytics: AnalyticsAdapter;
    driver: Neo4jDriverAdapter;
    logger: AbstractLogger;
    tokenProviders?: TokenProviders;
  };
  initialData?: {
    capabilities?: Capabilities.Capability[];
    configuration?: Configuration.PreloadedStateData;
    featureFlags?: FeatureFlags.PreloadedStateData;
    desktop?: Desktop.PreloadedStateData;
    guidePlaylists?: GuidePlaylists.PreloadedStateData;
  };
  meta?: {
    localStore?: boolean;
  };
}) {
  const reducer = createReducer();
  const store = configureStore({
    preloadedState: {
      capabilities: initialData?.capabilities,
      // HACK: Preloaded state MUST NOT have `_persist` property but TS requires it
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      configuration: Configuration.createInitialState(initialData?.configuration) as CombinedRootState['configuration'],
      // HACK: Preloaded state MUST NOT have `_persist` property but TS requires it
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      featureFlags: FeatureFlags.createInitialState(initialData?.featureFlags) as CombinedRootState['featureFlags'],
      // HACK: Preloaded state MUST NOT have `_persist` property but TS requires it
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      desktop: Desktop.createInitialState(initialData?.desktop) as CombinedRootState['desktop'],
      // HACK: Preloaded state MUST NOT have `_persist` property but TS requires it
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      guidePlaylists: GuidePlaylists.createInitialState(
        initialData?.guidePlaylists,
      ) as CombinedRootState['guidePlaylists'],
    },
    reducer,
    devTools: { name: 'Framework Root', trace: true },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [
            // redux-persist actions
            FLUSH,
            REHYDRATE,
            PAUSE,
            PERSIST,
            PURGE,
            REGISTER,
            // this action contains cypher query result
            'connections/runQuery/fulfilled',
          ],
        },
        thunk: {
          extraArgument: {
            driver: adapters.driver,
            logger: adapters.logger.child('redux'),
            tokenProviders: adapters.tokenProviders,
          },
        },
      })
        .prepend(
          createConfigurationListenerMiddleware(),
          createConnectionMiddleware({ driver: adapters.driver }),
          createAuraApiListenerMiddleware(),
          postConnectionMiddleware,
          createGuidesMiddleware(),
          createAnalyticsEventsMiddleware({ analytics: adapters.analytics }),
        )
        .concat(
          agentApi.middleware,
          guidesApi.middleware,
          GuidePlaylists.guidePlaylistsApi.middleware,
          consoleApi.middleware,
          relateApi.middleware,
          opsApi.middleware,
          fleetManagementApi.middleware,
          graphqlApi.middleware,
          eventListenerMiddleware.middleware,
          dataScienceApi.middleware,
          sharedStorageApi.middleware,
          stylingPrecedenceMiddleware.middleware,
        ),
  });

  const persistor = persistStore(store);

  setupListeners(store.dispatch);

  window.addEventListener('online', () => {
    store.dispatch(networkUp());
  });

  window.addEventListener('offline', () => {
    store.dispatch(networkDown());
  });

  if (meta?.localStore !== true) {
    exposeStoreInstance({ globalPersistor: persistor, globalStore: store });
  }

  return { persistor, store };
}

/**
 * Create mock implementation for `UNSAFE_getGlobalStoreInstance`
 *
 * This will create non-global store and return a function that should be
 * used instead of `UNSAFE_getGlobalStoreInstance` via mocking APIs like
 * `vi.mock` or `vi.spyOn`
 *
 * @example
 * vi.spyOn(StoreKit, 'UNSAFE_getGlobalStoreInstance')
 *   .mockImplementation(StoreKit.createGetGlobalStoreInstanceMock());
 */
export function createGetGlobalStoreInstanceMock() {
  const { persistor, store } = createStore({
    adapters: {
      analytics: new LoggerAnalyticsAdapter(),
      driver: neo4jDriverAdapter,
      logger: new NullLogger(),
    },
    meta: {
      localStore: true,
    },
  });
  return function getGlobalStoreInstanceMock() {
    return { globalPersistor: persistor, globalStore: store };
  };
}

/**
 * Enhanced root state (as returned from `combineReducers`)
 */
export type CombinedRootState = ReturnType<ReturnType<typeof createReducer>>;

export type Store = ReturnType<typeof createStore>['store'];

/**
 * Plain combined state
 *
 * Represents root state structure with pure state slices
 *
 * @see {@link CombinedRootState}
 */
export type RootState = {
  [Key in keyof CombinedRootState]: NoInfer<Omit<CombinedRootState[Key], keyof PersistPartial>>;
};

export type AppDispatch = Store['dispatch'];
